<template>
  <div>
    <BaseTable
      v-if="actions?.qstActionDetails"
      :headers="tableHeaders"
      :list="filteredData"
      @refresh-data="refreshData"
      @add-new="onAddNewActionDetail"
      table-id="actionDetails"
      clickable
      @table-item-clicked="onEditAction">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item)" />
        </div>
      </template>
    </BaseTable>
    <ActionDetailModalCreateEdit
      v-if="isModalOpen"
      :actionDetail="action"
      :title="
        actionModalMode == 'add'
          ? $t('qst-actions.addActionDetail')
          : action.isFromManager
            ? $t('qst-actions.editActionDetail')
            : $t('qst-actions.actionDetail')
      "
      :mode="actionModalMode"
      @close-modal="isModalOpen = false"
      @save="onSaveActionDetail" />
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>
<script setup lang="ts">
import { type QSTActionDetail } from '@/types/QSTActionDetails';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import ActionDetailModalCreateEdit from '@/components/questionnaires/ActionDetailModalCreateEdit.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { filterData, sortData } from '@/utils/table';
import { computed, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import type { QSTAction } from '@/types/QSTActions';
import { useI18n } from 'vue-i18n';

const dataQuery = ref<TableFilterData>();
const actions = defineModel<QSTAction>();
const isModalOpen = ref(false);
const actionModalMode = ref<'add' | 'edit'>('add');
const action = ref<QSTActionDetail>(initializeQSTActionDetail());

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  { value: 'sequence', text: $t('material.sequence'), type: 'text', active: true },
  { value: 'isSettled', text: $t('qst-actions.settled'), type: 'check', active: true },
  { value: 'description', text: $t('qst-actions.description'), type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];
const emit = defineEmits(['modified', 'deleted']);

function initializeQSTActionDetail(): QSTActionDetail {
  return {
    id: uuidv4(),
    description: '',
    isSettled: false,
    sequence: 0,
    isFromManager: true,
    isActive: false,
    qstActionID: actions.value ? actions.value?.id : '',
    rowStatus: RecordMode.Added,
  };
}

async function refreshData(data: TableFilterData) {
  dataQuery.value = data;
}
const filteredData = computed(() => {
  let finalData = Object.assign(
    actions.value?.qstActionDetails ? actions.value.qstActionDetails : []
  );
  finalData = finalData.map((item: QSTActionDetail) => {
    return { ...item, sequence: item.sequence.toString() };
  });

  finalData = finalData.filter((item: QSTActionDetail) => item.rowStatus != RecordMode.Deleted);
  if (dataQuery.value?.query) {
    finalData = filterData(finalData, dataQuery.value?.query, ['description']);
  }

  if (dataQuery.value?.column !== undefined && dataQuery.value?.order && finalData) {
    finalData = sortData(finalData, dataQuery.value?.column, dataQuery.value?.order, tableHeaders);
  }
  return finalData;
});

function onAddNewActionDetail() {
  action.value = initializeQSTActionDetail();
  isModalOpen.value = true;
  actionModalMode.value = 'add';
}

function onEditAction(item: QSTActionDetail) {
  action.value = item;
  actionModalMode.value = 'edit';
  isModalOpen.value = true;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<QSTActionDetail | null>(null);
function onDeleteRow(item: QSTActionDetail) {
  if (item) deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  if (!deleteRowItem.value || !actions.value) return;
  if (deleteRowItem.value.rowStatus == RecordMode.Added) {
    actions.value.qstActionDetails = actions.value.qstActionDetails.filter(
      (i) => i.id != deleteRowItem.value?.id
    );
  } else {
    if (deleteRowItem.value) deleteRowItem.value.rowStatus = RecordMode.Deleted;
    const index = actions.value.qstActionDetails.findIndex(
      (item) => item.id === deleteRowItem.value?.id
    );
    if (index != undefined && index != -1) {
      actions.value.qstActionDetails[index] = deleteRowItem.value;
    }
  }
  modalDeleteRow.value = false;
  emit('modified');
}

function onSaveActionDetail(modifiedData: QSTActionDetail) {
  const index = actions.value?.qstActionDetails.findIndex((item) => item.id === modifiedData.id);
  if (index != -1 && index != undefined && actions.value)
    actions.value.qstActionDetails[index] = modifiedData;
  else actions.value?.qstActionDetails.push(modifiedData);
  isModalOpen.value = false;
  emit('modified');
}
</script>
[]
